import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, IconButton, Paper, Chip,
  CircularProgress, Tooltip, Divider, Alert, Tabs, Tab, Dialog,
  DialogTitle, DialogContent, DialogActions, Badge
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Send as SendIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AutoAwesome as AiIcon,
  Description as DocIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  FolderOpen as FolderIcon,
  NoteAdd as NoteIcon,
  Visibility as ViewIcon,
  Share as ShareIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import axios from 'axios';
import AuthContext from '../contexts/AuthContext';

// ── Tipi ──────────────────────────────────────────────────────────────────────
interface Documento {
  id: string;
  title: string;
  description: string | null;
  cities: string[];
  uploadDate: string;
  keywords: string[];
}

interface FascicoloDoc {
  id: string;
  documentId: string;
  nota: string;
  addedAt: string;
  document: Documento;
}

interface AiChat {
  id: string;
  domanda: string;
  risposta: string;
  createdAt: string;
}

interface Fascicolo {
  id: string;
  nome: string;
  colore: string;
  appunti: string;
  createdAt: string;
  updatedAt: string;
  documenti: FascicoloDoc[];
  aiChats: AiChat[];
}

interface SearchDoc {
  id: string;
  title: string;
  snippet: string;
  cities: string[];
}

interface SearchMessage {
  role: 'user' | 'assistant';
  content: string;
  documents?: SearchDoc[];
  totalFound?: number;
}

// ── Formattazione markdown ─────────────────────────────────────────────────────
const formatMarkdown = (text: string) =>
  text
    .replace(/^### (.*$)/gm, '<strong style="font-size:1.05em;display:block;margin:12px 0 6px">$1</strong>')
    .replace(/^## (.*$)/gm, '<strong style="font-size:1.1em;display:block;margin:16px 0 8px;color:#1B2A4A">$1</strong>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^[-•] (.*$)/gm, '<div style="display:flex;gap:8px;margin:4px 0"><span style="color:#2563EB;font-weight:bold;flex-shrink:0">•</span><span>$1</span></div>')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');

// ── Componente principale ──────────────────────────────────────────────────────
const FascicoloPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const headers = { Authorization: `Bearer ${token}` };

  // Fascicolo
  const [fascicolo, setFascicolo] = useState<Fascicolo | null>(null);
  const [loading, setLoading] = useState(true);

  // Tab pannello destro: 0=Sentenze, 1=Appunti, 2=AI
  const [tabDx, setTabDx] = useState(0);

  // Appunti autosave
  const [appunti, setAppunti] = useState('');
  const [salvandoAppunti, setSalvandoAppunti] = useState(false);
  const appuntiTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Note su sentenza
  const [notaEdit, setNotaEdit] = useState<{ docId: string; testo: string } | null>(null);

  // BOOKY Search (pannello sinistro)
  const [searchInput, setSearchInput] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchMessages, setSearchMessages] = useState<SearchMessage[]>([]);
  const [searchSessionId, setSearchSessionId] = useState<string | null>(null);
  const [aggiungendoDoc, setAggiungendoDoc] = useState<string | null>(null);
  const searchEndRef = useRef<HTMLDivElement>(null);

  // Dialog nota all'aggiunta
  const [dialogNota, setDialogNota] = useState<{ docId: string; docTitle: string } | null>(null);
  const [notaTemp, setNotaTemp] = useState('');

  // AI del fascicolo
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const aiEndRef = useRef<HTMLDivElement>(null);

  // Export link pubblico
  const [exportLoading, setExportLoading] = useState(false);
  const [linkCopiato, setLinkCopiato] = useState(false);
  const [linkPubblico, setLinkPubblico] = useState<string | null>(null);
  const [dialogLinkOpen, setDialogLinkOpen] = useState(false);

  // ── Caricamento fascicolo ──────────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { caricaFascicolo(); }, [id]);

  const caricaFascicolo = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/fascicoli/${id}`, { headers });
      setFascicolo(res.data.fascicolo);
      setAppunti(res.data.fascicolo.appunti || '');
    } catch {
      navigate('/booky-work');
    } finally {
      setLoading(false);
    }
  };

  // ── Autosave appunti ──────────────────────────────────────────────────────
  const handleAppuntiChange = (val: string) => {
    setAppunti(val);
    if (appuntiTimer.current) clearTimeout(appuntiTimer.current);
    appuntiTimer.current = setTimeout(() => salvaAppunti(val), 1500);
  };

  const salvaAppunti = async (testo: string) => {
    try {
      setSalvandoAppunti(true);
      await axios.put(`/api/fascicoli/${id}`, { appunti: testo }, { headers });
    } finally {
      setSalvandoAppunti(false);
    }
  };

  // ── BOOKY Search ──────────────────────────────────────────────────────────
  useEffect(() => {
    searchEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [searchMessages]);

  const eseguiRicerca = async () => {
    if (!searchInput.trim() || searchLoading) return;
    const domanda = searchInput.trim();
    setSearchInput('');
    setSearchLoading(true);
    setSearchMessages(prev => [...prev, { role: 'user', content: domanda }]);
    try {
      const res = await axios.post(
        '/api/booky-search',
        { message: domanda, sessionId: searchSessionId },
        { headers }
      );
      setSearchSessionId(res.data.sessionId);
      setSearchMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.response,
        documents: res.data.documents,
        totalFound: res.data.totalFound
      }]);
    } catch (e: any) {
      setSearchMessages(prev => [...prev, {
        role: 'assistant',
        content: e.response?.data?.message || 'Errore nella ricerca. Riprova.'
      }]);
    } finally {
      setSearchLoading(false);
    }
  };

  // ── Aggiungi documento al fascicolo ──────────────────────────────────────
  const aggiungiDocumento = async (docId: string, docTitle: string) => {
    setDialogNota({ docId, docTitle });
    setNotaTemp('');
  };

  const confermaAggiunta = async () => {
    if (!dialogNota) return;
    try {
      setAggiungendoDoc(dialogNota.docId);
      const res = await axios.post(
        `/api/fascicoli/${id}/documenti`,
        { documentId: dialogNota.docId, nota: notaTemp },
        { headers }
      );
      setFascicolo(prev => prev ? {
        ...prev,
        documenti: [res.data.documento, ...prev.documenti.filter(d => d.documentId !== dialogNota.docId)]
      } : prev);
      setTabDx(0);
    } catch (e) {
      console.error(e);
    } finally {
      setAggiungendoDoc(null);
      setDialogNota(null);
    }
  };

  // ── Rimuovi documento ─────────────────────────────────────────────────────
  const rimuoviDocumento = async (documentId: string) => {
    try {
      await axios.delete(`/api/fascicoli/${id}/documenti/${documentId}`, { headers });
      setFascicolo(prev => prev ? {
        ...prev,
        documenti: prev.documenti.filter(d => d.documentId !== documentId)
      } : prev);
    } catch (e) { console.error(e); }
  };

  // ── Salva nota documento ──────────────────────────────────────────────────
  const salvaNota = async () => {
    if (!notaEdit) return;
    try {
      await axios.put(
        `/api/fascicoli/${id}/documenti/${notaEdit.docId}`,
        { nota: notaEdit.testo },
        { headers }
      );
      setFascicolo(prev => prev ? {
        ...prev,
        documenti: prev.documenti.map(d =>
          d.documentId === notaEdit.docId ? { ...d, nota: notaEdit.testo } : d
        )
      } : prev);
      setNotaEdit(null);
    } catch (e) { console.error(e); }
  };

  // ── AI del fascicolo ──────────────────────────────────────────────────────
  useEffect(() => {
    aiEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [fascicolo?.aiChats]);

  const inviaAiDomanda = async () => {
    if (!aiInput.trim() || aiLoading) return;
    const domanda = aiInput.trim();
    setAiInput('');
    setAiLoading(true);
    try {
      const res = await axios.post(`/api/fascicoli/${id}/ai`, { domanda }, { headers });
      setFascicolo(prev => prev ? {
        ...prev,
        aiChats: [...prev.aiChats, {
          id: res.data.chatId,
          domanda,
          risposta: res.data.risposta,
          createdAt: new Date().toISOString()
        }]
      } : prev);
      setTabDx(2);
    } catch (e: any) {
      alert(e.response?.data?.message || 'Errore AI');
    } finally {
      setAiLoading(false);
    }
  };

  // ── Genera link pubblico ──────────────────────────────────────────────────
  const copiaLink = (testo: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(testo);
      } else {
        const el = document.createElement('textarea');
        el.value = testo;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.focus();
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setLinkCopiato(true);
      setTimeout(() => setLinkCopiato(false), 3000);
    } catch (e) {
      console.warn('Clipboard non disponibile:', e);
    }
  };

  const generaLink = async () => {
    try {
      setExportLoading(true);
      const res = await axios.post(`/api/fascicoli/${id}/export`, {}, { headers });
      const url = `${window.location.origin}/fascicolo-pubblico/${res.data.token}`;
      setLinkPubblico(url);
      setDialogLinkOpen(true);
    } catch (e: any) {
      console.error('[EXPORT]', e);
      alert('Errore nella generazione del link. Riprova.');
    } finally {
      setExportLoading(false);
    }
  };

  const eliminaAiChat = async (chatId: string) => {
    try {
      await axios.delete(`/api/fascicoli/${id}/ai/${chatId}`, { headers });
      setFascicolo(prev => prev ? {
        ...prev,
        aiChats: prev.aiChats.filter(c => c.id !== chatId)
      } : prev);
    } catch (e) { console.error(e); }
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
      <CircularProgress />
    </Box>
  );
  if (!fascicolo) return null;

  const isDocNelFascicolo = (docId: string) => fascicolo.documenti.some(d => d.documentId === docId);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <Box sx={{
        px: 3, py: 1.5,
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        display: 'flex', alignItems: 'center', gap: 2,
        background: 'white', flexShrink: 0
      }}>
        <IconButton onClick={() => navigate('/booky-work')} size="small">
          <BackIcon />
        </IconButton>
        <Box sx={{ width: 6, height: 32, borderRadius: 1, bgcolor: fascicolo.colore }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={800} noWrap>{fascicolo.nome}</Typography>
          <Typography variant="caption" color="text.secondary">
            {fascicolo.documenti.length} {fascicolo.documenti.length === 1 ? 'sentenza' : 'sentenze'} · {fascicolo.aiChats.length} conversazioni AI
          </Typography>
        </Box>
        <Tooltip title={linkCopiato ? 'Link copiato!' : 'Genera e condividi link pubblico del fascicolo'}>
          <Button
            size="small"
            variant="outlined"
            onClick={generaLink}
            disabled={exportLoading}
            startIcon={
              exportLoading
                ? <CircularProgress size={14} color="inherit" />
                : linkCopiato
                  ? <CheckIcon sx={{ fontSize: 16, color: '#059669' }} />
                  : <ShareIcon sx={{ fontSize: 16 }} />
            }
            sx={{
              textTransform: 'none', fontWeight: 700, fontSize: 12, borderRadius: 2,
              borderColor: linkCopiato ? '#059669' : 'rgba(0,0,0,0.2)',
              color: linkCopiato ? '#059669' : 'text.secondary',
              '&:hover': { borderColor: fascicolo.colore, color: fascicolo.colore, bgcolor: fascicolo.colore + '10' }
            }}
          >
            {exportLoading ? 'Generazione...' : linkCopiato ? 'Link copiato!' : 'Condividi'}
          </Button>
        </Tooltip>
        <Chip
          label="BOOKY Work"
          size="small"
          icon={<FolderIcon sx={{ fontSize: '14px !important' }} />}
          sx={{ bgcolor: fascicolo.colore + '18', color: fascicolo.colore, fontWeight: 700, fontSize: 12 }}
        />
      </Box>

      {/* ── SPLIT SCREEN ────────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ════════════════════════════════════════════════════════════════════
            PANNELLO SINISTRO — BOOKY SEARCH
        ════════════════════════════════════════════════════════════════════ */}
        <Box sx={{
          width: '50%', display: 'flex', flexDirection: 'column',
          borderRight: '2px solid rgba(0,0,0,0.06)', overflow: 'hidden', bgcolor: '#FAFBFF'
        }}>
          {/* Header sx */}
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.06)', bgcolor: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 28, height: 28, borderRadius: 1,
                background: 'linear-gradient(135deg, #FFD700, #F59E0B)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <AiIcon sx={{ fontSize: 16, color: '#1B2A4A' }} />
              </Box>
              <Typography variant="subtitle2" fontWeight={800}>BOOKY Search</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.3, display: 'block' }}>
              Cerca le sentenze che ti servono. Clicca <strong>"Aggiungi al fascicolo"</strong> per salvarle nel caso.
            </Typography>
          </Box>

          {/* Messaggi ricerca */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {searchMessages.length === 0 && (
              <Box sx={{
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', py: 4, textAlign: 'center'
              }}>
                <AiIcon sx={{ fontSize: 40, color: '#FFD700', mb: 2 }} />
                <Typography variant="body2" fontWeight={600} color="text.secondary" gutterBottom>
                  Inizia una ricerca
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ maxWidth: 260 }}>
                  Scrivi un argomento giuridico, un articolo di legge o una parola chiave. 
                  Trovi le sentenze rilevanti e le aggiungi al tuo fascicolo con un click.
                </Typography>
              </Box>
            )}

            {searchMessages.map((msg, i) => (
              <Box key={i}>
                {msg.role === 'user' ? (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Box sx={{
                      maxWidth: '85%', bgcolor: '#1B2A4A', color: 'white',
                      borderRadius: '12px 12px 2px 12px', px: 2, py: 1.5
                    }}>
                      <Typography variant="body2">{msg.content}</Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    {/* Risposta AI */}
                    <Box sx={{
                      bgcolor: 'white', border: '1px solid rgba(0,0,0,0.08)',
                      borderRadius: '2px 12px 12px 12px', px: 2, py: 1.5, mb: 1.5,
                      '& p': { m: 0, mb: 1, fontSize: '0.875rem', lineHeight: 1.7, '&:last-child': { mb: 0 } },
                      '& strong': { fontWeight: 700, color: 'var(--primary-color)' },
                      '& em': { fontStyle: 'italic', color: 'var(--text-secondary)' },
                      '& ul, & ol': { pl: 2.5, mb: 1, fontSize: '0.875rem' },
                      '& li': { mb: 0.5 },
                      '& h1, & h2, & h3': { fontFamily: 'Syne, sans-serif', fontWeight: 700, color: 'var(--primary-color)', mt: 1.5, mb: 0.5, fontSize: '0.95rem' },
                      '& hr': { borderColor: 'var(--border-light)', my: 1 },
                    }}>
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                      {msg.totalFound !== undefined && (
                        <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
                          {msg.totalFound} sentenze trovate nel database
                        </Typography>
                      )}
                    </Box>

                    {/* Documenti trovati con pulsante aggiungi */}
                    {msg.documents && msg.documents.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {msg.documents.map((doc) => {
                          const giàAggiunto = isDocNelFascicolo(doc.id);
                          return (
                            <Paper key={doc.id} elevation={0} sx={{
                              p: 1.5, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2,
                              borderLeft: `3px solid ${giàAggiunto ? fascicolo.colore : '#e2e8f0'}`
                            }}>
                              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5, fontSize: 13, lineHeight: 1.3 }}>
                                {doc.title}
                              </Typography>
                              {doc.snippet && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  component="div"
                                  sx={{ mb: 1, lineHeight: 1.5 }}
                                  dangerouslySetInnerHTML={{ __html: doc.snippet }}
                                />
                              )}
                              {doc.cities?.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                                  {doc.cities.map(c => (
                                    <Chip key={c} label={c} size="small" sx={{ fontSize: 11, height: 20 }} />
                                  ))}
                                </Box>
                              )}
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  size="small"
                                  startIcon={<ViewIcon sx={{ fontSize: 14 }} />}
                                  onClick={() => window.open(`/viewer/${doc.id}`, '_blank')}
                                  sx={{ textTransform: 'none', fontSize: 12, py: 0.3 }}
                                >
                                  Leggi
                                </Button>
                                <Button
                                  size="small"
                                  variant={giàAggiunto ? 'outlined' : 'contained'}
                                  startIcon={
                                    aggiungendoDoc === doc.id
                                      ? <CircularProgress size={12} color="inherit" />
                                      : giàAggiunto ? <DocIcon sx={{ fontSize: 14 }} /> : <AddIcon sx={{ fontSize: 14 }} />
                                  }
                                  disabled={aggiungendoDoc === doc.id}
                                  onClick={() => !giàAggiunto && aggiungiDocumento(doc.id, doc.title)}
                                  sx={{
                                    textTransform: 'none', fontSize: 12, py: 0.3,
                                    ...(giàAggiunto ? {
                                      borderColor: fascicolo.colore, color: fascicolo.colore
                                    } : {
                                      bgcolor: fascicolo.colore,
                                      '&:hover': { bgcolor: fascicolo.colore, filter: 'brightness(0.85)' }
                                    })
                                  }}
                                >
                                  {giàAggiunto ? 'Nel fascicolo ✓' : 'Aggiungi al fascicolo'}
                                </Button>
                              </Box>
                            </Paper>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            ))}

            {searchLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">BOOKY sta cercando...</Typography>
              </Box>
            )}
            <div ref={searchEndRef} />
          </Box>

          {/* Input ricerca */}
          <Box sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.06)', bgcolor: 'white' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Cerca sentenze… (es. licenziamento per giusta causa)"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && eseguiRicerca()}
                disabled={searchLoading}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <IconButton
                onClick={eseguiRicerca}
                disabled={!searchInput.trim() || searchLoading}
                sx={{
                  bgcolor: '#1B2A4A', color: 'white', borderRadius: 2,
                  '&:hover': { bgcolor: '#2c4270' },
                  '&:disabled': { bgcolor: 'rgba(0,0,0,0.12)' }
                }}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* ════════════════════════════════════════════════════════════════════
            PANNELLO DESTRO — IL MIO CASO
        ════════════════════════════════════════════════════════════════════ */}
        <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: 'white' }}>

          {/* Tabs */}
          <Box sx={{ borderBottom: '1px solid rgba(0,0,0,0.06)', px: 1 }}>
            <Tabs
              value={tabDx}
              onChange={(_, v) => setTabDx(v)}
              sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 44 } }}
            >
              <Tab
                label={
                  <Badge badgeContent={fascicolo.documenti.length} color="primary" max={99}
                    sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}>
                    <Box sx={{ mr: 1 }}>Sentenze</Box>
                  </Badge>
                }
              />
              <Tab label="Appunti" />
              <Tab
                label={
                  <Badge badgeContent={fascicolo.aiChats.length} color="secondary" max={99}
                    sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16, bgcolor: '#7C3AED' } }}>
                    <Box sx={{ mr: 1 }}>AI del caso</Box>
                  </Badge>
                }
              />
            </Tabs>
          </Box>

          {/* ── TAB 0: SENTENZE ──────────────────────────────────────────────── */}
          {tabDx === 0 && (
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {fascicolo.documenti.length === 0 ? (
                <Box sx={{
                  flex: 1, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', py: 4, textAlign: 'center'
                }}>
                  <DocIcon sx={{ fontSize: 40, color: 'rgba(0,0,0,0.15)', mb: 2 }} />
                  <Typography variant="body2" fontWeight={600} color="text.secondary" gutterBottom>
                    Nessuna sentenza nel fascicolo
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ maxWidth: 260 }}>
                    Usa BOOKY Search a sinistra per trovare le sentenze che ti servono, poi clicca
                    <strong> "Aggiungi al fascicolo"</strong>.
                  </Typography>
                </Box>
              ) : (
                fascicolo.documenti.map((fd) => (
                  <Paper key={fd.documentId} elevation={0} sx={{
                    p: 2, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2,
                    borderLeft: `3px solid ${fascicolo.colore}`
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body2" fontWeight={700} sx={{ flex: 1, lineHeight: 1.4, mr: 1 }}>
                        {fd.document.title}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                        <Tooltip title="Leggi la sentenza">
                          <IconButton size="small" onClick={() => window.open(`/viewer/${fd.documentId}`, '_blank')}>
                            <ViewIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modifica nota">
                          <IconButton size="small" onClick={() => setNotaEdit({ docId: fd.documentId, testo: fd.nota || '' })}>
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Rimuovi dal fascicolo">
                          <IconButton size="small" onClick={() => rimuoviDocumento(fd.documentId)} sx={{ color: 'error.main' }}>
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {fd.document.cities?.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                        {fd.document.cities.map(c => (
                          <Chip key={c} label={c} size="small" sx={{ fontSize: 11, height: 18 }} />
                        ))}
                      </Box>
                    )}

                    {/* Nota */}
                    {notaEdit?.docId === fd.documentId ? (
                      <Box>
                        <TextField
                          fullWidth multiline rows={2} size="small"
                          placeholder="Aggiungi una nota su questa sentenza…"
                          value={notaEdit.testo}
                          onChange={e => setNotaEdit({ ...notaEdit, testo: e.target.value })}
                          sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button size="small" variant="contained" onClick={salvaNota}
                            startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
                            sx={{ textTransform: 'none', fontSize: 12, py: 0.4, bgcolor: fascicolo.colore,
                              '&:hover': { bgcolor: fascicolo.colore, filter: 'brightness(0.85)' } }}>
                            Salva nota
                          </Button>
                          <Button size="small" onClick={() => setNotaEdit(null)}
                            sx={{ textTransform: 'none', fontSize: 12, py: 0.4 }}>
                            Annulla
                          </Button>
                        </Box>
                      </Box>
                    ) : fd.nota ? (
                      <Box sx={{
                        bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, px: 1.5, py: 1,
                        display: 'flex', alignItems: 'flex-start', gap: 1
                      }}>
                        <NoteIcon sx={{ fontSize: 14, color: 'text.disabled', mt: 0.2, flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                          {fd.nota}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography
                        variant="caption" color="text.disabled"
                        sx={{ cursor: 'pointer', '&:hover': { color: 'text.secondary' } }}
                        onClick={() => setNotaEdit({ docId: fd.documentId, testo: '' })}
                      >
                        + Aggiungi una nota
                      </Typography>
                    )}
                  </Paper>
                ))
              )}
            </Box>
          )}

          {/* ── TAB 1: APPUNTI ─────────────────────────────────────────────── */}
          {tabDx === 1 && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  Appunti liberi del caso
                </Typography>
                {salvandoAppunti && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CircularProgress size={12} />
                    <Typography variant="caption" color="text.disabled">Salvataggio...</Typography>
                  </Box>
                )}
              </Box>
              <TextField
                fullWidth
                multiline
                placeholder={`Scrivi qui le tue note sul fascicolo "${fascicolo.nome}"…\n\nPuoi annotare strategie difensive, punti di forza, domande per il cliente, cose da verificare — tutto quello che ti è utile per il caso. Si salva automaticamente.`}
                value={appunti}
                onChange={e => handleAppuntiChange(e.target.value)}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': { height: '100%', alignItems: 'flex-start', borderRadius: 2 },
                  '& .MuiInputBase-input': { height: '100% !important', overflowY: 'auto !important' }
                }}
                InputProps={{ sx: { flex: 1 } }}
              />
            </Box>
          )}

          {/* ── TAB 2: AI DEL CASO ─────────────────────────────────────────── */}
          {tabDx === 2 && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

                {fascicolo.documenti.length === 0 && (
                  <Alert severity="info" sx={{ borderRadius: 2, fontSize: 13 }}>
                    Aggiungi almeno una sentenza al fascicolo prima di interrogare l'AI del caso.
                  </Alert>
                )}

                {fascicolo.aiChats.length === 0 && fascicolo.documenti.length > 0 && (
                  <Box sx={{
                    flex: 1, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', py: 4, textAlign: 'center'
                  }}>
                    <AiIcon sx={{ fontSize: 40, color: '#7C3AED', mb: 2 }} />
                    <Typography variant="body2" fontWeight={600} color="text.secondary" gutterBottom>
                      AI del fascicolo pronta
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ maxWidth: 280, lineHeight: 1.6 }}>
                      Puoi fare domande ragionate su tutte le {fascicolo.documenti.length} sentenze del fascicolo.
                      L'AI ragiona solo su quelle — non inventa nulla.
                      Ogni conversazione viene salvata qui.
                    </Typography>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1, width: '100%', maxWidth: 320 }}>
                      {[
                        'Quali sentenze supportano la mia tesi principale?',
                        'Ci sono orientamenti contraddittori tra queste sentenze?',
                        'Riassumimi i punti chiave comuni'
                      ].map(sug => (
                        <Button
                          key={sug} size="small" variant="outlined"
                          onClick={() => { setAiInput(sug); }}
                          sx={{
                            textTransform: 'none', fontSize: 12, borderRadius: 2,
                            borderColor: 'rgba(124,58,237,0.3)', color: '#7C3AED',
                            textAlign: 'left', justifyContent: 'flex-start', px: 1.5,
                            '&:hover': { borderColor: '#7C3AED', bgcolor: 'rgba(124,58,237,0.05)' }
                          }}
                        >
                          {sug}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                )}

                {fascicolo.aiChats.map((chat) => (
                  <Box key={chat.id}>
                    {/* Domanda */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                      <Box sx={{
                        maxWidth: '85%', bgcolor: '#1B2A4A', color: 'white',
                        borderRadius: '12px 12px 2px 12px', px: 2, py: 1.5
                      }}>
                        <Typography variant="body2">{chat.domanda}</Typography>
                      </Box>
                    </Box>

                    {/* Risposta */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <Box sx={{
                        width: 28, height: 28, borderRadius: 1,
                        background: 'linear-gradient(135deg, #7C3AED, #9333EA)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.3
                      }}>
                        <AiIcon sx={{ fontSize: 16, color: 'white' }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{
                          bgcolor: 'rgba(124,58,237,0.04)', border: '1px solid rgba(124,58,237,0.12)',
                          borderRadius: '2px 12px 12px 12px', px: 2, py: 1.5,
                          '& p': { m: 0, mb: 1, fontSize: '0.875rem', lineHeight: 1.7, '&:last-child': { mb: 0 } },
                          '& strong': { fontWeight: 700, color: '#7C3AED' },
                          '& em': { fontStyle: 'italic', color: 'var(--text-secondary)' },
                          '& ul, & ol': { pl: 2.5, mb: 1, fontSize: '0.875rem' },
                          '& li': { mb: 0.5 },
                          '& h1, & h2, & h3': { fontFamily: 'Syne, sans-serif', fontWeight: 700, color: '#7C3AED', mt: 1.5, mb: 0.5, fontSize: '0.95rem' },
                          '& hr': { borderColor: 'var(--border-light)', my: 1 },
                        }}>
                          <ReactMarkdown>{chat.risposta}</ReactMarkdown>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, px: 0.5 }}>
                          <Typography variant="caption" color="text.disabled">
                            {new Date(chat.createdAt).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                          <Tooltip title="Elimina questa conversazione">
                            <IconButton size="small" onClick={() => eliminaAiChat(chat.id)} sx={{ opacity: 0.4, '&:hover': { opacity: 1, color: 'error.main' } }}>
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))}

                {aiLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                      width: 28, height: 28, borderRadius: 1,
                      background: 'linear-gradient(135deg, #7C3AED, #9333EA)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <AiIcon sx={{ fontSize: 16, color: 'white' }} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {[0, 1, 2].map(i => (
                        <Box key={i} sx={{
                          width: 6, height: 6, borderRadius: '50%', bgcolor: '#7C3AED',
                          animation: 'pulse 1.4s infinite', animationDelay: `${i * 0.2}s`,
                          '@keyframes pulse': {
                            '0%, 80%, 100%': { opacity: 0.2 },
                            '40%': { opacity: 1 }
                          }
                        }} />
                      ))}
                    </Box>
                  </Box>
                )}
                <div ref={aiEndRef} />
              </Box>

              {/* Input AI */}
              <Box sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth size="small" multiline maxRows={3}
                    placeholder={fascicolo.documenti.length === 0
                      ? 'Aggiungi prima le sentenze al fascicolo…'
                      : `Fai una domanda all'AI sulle ${fascicolo.documenti.length} sentenze del caso…`
                    }
                    value={aiInput}
                    onChange={e => setAiInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && inviaAiDomanda()}
                    disabled={aiLoading || fascicolo.documenti.length === 0}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <IconButton
                    onClick={inviaAiDomanda}
                    disabled={!aiInput.trim() || aiLoading || fascicolo.documenti.length === 0}
                    sx={{
                      bgcolor: '#7C3AED', color: 'white', borderRadius: 2, alignSelf: 'flex-end',
                      '&:hover': { bgcolor: '#6D28D9' },
                      '&:disabled': { bgcolor: 'rgba(0,0,0,0.12)' }
                    }}
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── DIALOG: Link pubblico ───────────────────────────────────────────── */}
      <Dialog
        open={dialogLinkOpen}
        onClose={() => setDialogLinkOpen(false)}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShareIcon sx={{ color: fascicolo?.colore }} />
            Link pubblico generato
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Chiunque abbia questo link può vedere il fascicolo, i riepiloghi AI e le conversazioni — senza fare login.
          </Typography>
          <Box sx={{
            display: 'flex', gap: 1, alignItems: 'center',
            bgcolor: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.1)',
            borderRadius: 2, p: 1.5
          }}>
            <Typography
              variant="body2"
              sx={{
                flex: 1, fontFamily: 'monospace', fontSize: 12,
                wordBreak: 'break-all', color: '#1B2A4A', fontWeight: 600
              }}
            >
              {linkPubblico}
            </Typography>
            <Tooltip title={linkCopiato ? 'Copiato!' : 'Copia link'}>
              <IconButton
                size="small"
                onClick={() => linkPubblico && copiaLink(linkPubblico)}
                sx={{
                  bgcolor: linkCopiato ? '#059669' : fascicolo?.colore,
                  color: 'white', borderRadius: 1.5, flexShrink: 0,
                  '&:hover': { opacity: 0.85 }
                }}
              >
                {linkCopiato ? <CheckIcon sx={{ fontSize: 18 }} /> : <CopyIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
          </Box>
          {linkCopiato && (
            <Typography variant="caption" sx={{ color: '#059669', mt: 1, display: 'block', fontWeight: 600 }}>
              ✓ Link copiato negli appunti
            </Typography>
          )}
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1.5, display: 'block' }}>
            Il link è permanente. Puoi condividerlo via email, WhatsApp o qualsiasi altro canale.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => linkPubblico && window.open(linkPubblico, '_blank')}
            startIcon={<ViewIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Apri anteprima
          </Button>
          <Button
            variant="contained"
            onClick={() => linkPubblico && copiaLink(linkPubblico)}
            startIcon={linkCopiato ? <CheckIcon /> : <CopyIcon />}
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 2,
              bgcolor: linkCopiato ? '#059669' : fascicolo?.colore,
              '&:hover': { opacity: 0.85 }
            }}
          >
            {linkCopiato ? 'Copiato!' : 'Copia link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DIALOG: Nota all'aggiunta di una sentenza ────────────────────────── */}
      <Dialog
        open={!!dialogNota}
        onClose={() => setDialogNota(null)}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Aggiungi al fascicolo
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Sentenza selezionata:
          </Typography>
          <Typography variant="body2" fontWeight={700} sx={{ mb: 2, lineHeight: 1.4 }}>
            {dialogNota?.docTitle}
          </Typography>
          <TextField
            fullWidth multiline rows={2} autoFocus
            label="Nota personale (facoltativa)"
            placeholder="Es. Da citare nel ricorso, contraddice la tesi avversaria…"
            value={notaTemp}
            onChange={e => setNotaTemp(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setDialogNota(null)} sx={{ textTransform: 'none' }}>Annulla</Button>
          <Button
            variant="contained"
            onClick={confermaAggiunta}
            startIcon={<AddIcon />}
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 2,
              bgcolor: fascicolo?.colore,
              '&:hover': { bgcolor: fascicolo?.colore, filter: 'brightness(0.85)' }
            }}
          >
            Aggiungi al fascicolo
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default FascicoloPage;
